import { useCallback, useMemo } from 'react';
import { useConsent } from 'c15t/react';
import { queuePixelEvent } from 'src/features/consent/utils';
import type { Reddit } from './types';

/**
 * Reddit conversion tracking, consent-aware.
 *
 * The Reddit Pixel is hard-gated on `marketing`, so `window.rdt` does not exist
 * until consent is granted. Previously this hook logged a console error in that
 * case and the event was lost — which is precisely how `reddit.track('Lead')`
 * in the sign-up form would silently stop reporting leads.
 *
 * Instead, events raised before consent are queued and replayed if the visitor
 * later accepts. A visitor who never accepts never sends one, which is the
 * point.
 */
export function useReddit(): Reddit {
  const marketing = useConsent('marketing');

  const track = useCallback<Reddit['track']>(
    (event) => {
      const send = () => {
        if (typeof window.rdt === 'function') {
          window.rdt('track', event);
        }
      };

      if (marketing && typeof window.rdt === 'function') {
        send();

        return;
      }

      queuePixelEvent({ vendor: 'reddit', send });
    },
    [marketing],
  );

  return useMemo(() => ({ track }), [track]);
}
